/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
    Text,
    Textarea,
} from '@fluentui/react-components';
import { Status20Regular } from '@fluentui/react-icons';
import { Column, Row } from '../../common';
import { CaseStatus, CaseSystemStatus, useInitialValues } from '../../../data/types';
import { useFormik } from 'formik';
import { useCaseDataService } from '../../../data/services';
import { useToasts } from '../../toasts/useToasts';
import { useValidation } from '../../../data/types/validation';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
});

export type AddEditCaseStatusDialogProps = Partial<DialogProps> & {
    systemStatus: CaseSystemStatus;
    children?: ReactElement<any>;
    statusToEdit?: CaseStatus;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onSubmit?: (status: CaseStatus) => Promise<void>;
};
export const AddEditCaseStatusDialog = (props: AddEditCaseStatusDialogProps) => {
    const { children, statusToEdit, systemStatus, open = false, onOpenChange } = props;

    //Hooks
    const initValues = useInitialValues();
    const caseDataService = useCaseDataService();
    const toasts = useToasts();
    const { validateCaseStatus } = useValidation();

    //Vars
    const userCreatedStatuses = caseDataService.userCreatedStatuses
        .filter((x) => x.systemStatus == systemStatus)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    //State
    const [isOpen, setIsOpen] = useState<boolean>(open);

    //Styles
    const styles = useStyles();

    //Form
    const getIndex = (cs: CaseStatus) => {
        return userCreatedStatuses.findIndex((x) => x.systemStatus == cs.systemStatus && x.status == cs.status);
    };
    const initStatus = (): CaseStatus => {
        return {
            ...initValues.caseStatus(systemStatus),
            ...(statusToEdit ?? {}),
        };
    };
    const statusForm = useFormik<CaseStatus>({
        initialValues: initStatus(),
        onSubmit: async (values, { resetForm }) => {
            const caseStatus = values as CaseStatus;

            if (!!caseStatus.status) {
                caseStatus.systemStatus = systemStatus;
                if (!statusToEdit) {
                    caseStatus.order = userCreatedStatuses.length;
                } else {
                    const index = getIndex(caseStatus);
                    caseStatus.order = index;
                }

                setIsOpen(false);

                try {
                    await caseDataService.addCaseStatus(caseStatus);

                    toasts.caseStatus.notifyCaseStatusCreated(`${caseStatus.status} (${caseStatus.systemStatus})`);
                } catch {
                    toasts.caseStatus.notifyCaseStatusCreateError(`${caseStatus.status} (${caseStatus.systemStatus})`);
                }

                resetForm({ values: initStatus() });
            }
        },
        validationSchema: validateCaseStatus.schema,
    });

    //UI Events
    const clickSave = (e: React.MouseEvent) => {
        e.stopPropagation();
        statusForm.submitForm();
    };
    const clickCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        statusForm.resetForm({ values: { ...initStatus() } });
    };
    const onDialogOpenChange = (e: React.SyntheticEvent, data: { open: boolean }) => {
        e.stopPropagation();
        onOpenChange ? onOpenChange(data.open) : setIsOpen(data.open);
    };

    //Effects
    useEffect(() => {
        if (!!isOpen && !!statusToEdit) {
            statusForm.resetForm({ values: { ...initStatus() } });
        }
    }, [isOpen, statusToEdit]);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    return (
        <>
            <Dialog modalType='alert' open={isOpen} onOpenChange={onDialogOpenChange}>
                <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            <Row gap='medium'>
                                <Column width='auto' vAlign='middle'>
                                    <Status20Regular />
                                </Column>
                                <Column>{!!statusToEdit ? 'Edit Status' : `Create new '${systemStatus}' Status`}</Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent>
                            <Column gap='medium'>
                                <Field className={styles.field} label='System Status'>
                                    <Text size={500} weight='semibold'>
                                        {systemStatus}
                                    </Text>
                                </Field>
                                <Field
                                    className={styles.field}
                                    label='Status'
                                    required
                                    validationMessage={statusForm.errors.status}
                                >
                                    {!statusToEdit ? (
                                        <Input
                                            name='status'
                                            value={statusForm.values.status || ''}
                                            onChange={statusForm.handleChange}
                                            tabIndex={1}
                                            placeholder='Enter the status'
                                            maxLength={validateCaseStatus.status.maxLength}
                                            data-testid='add-edit-status-status-input'
                                        />
                                    ) : (
                                        <Text size={500} weight='semibold'>
                                            {statusForm.values.status}
                                        </Text>
                                    )}
                                </Field>
                                <Field
                                    className={styles.field}
                                    label='Description'
                                    hint={`${statusForm.values.description?.length || 0}/${validateCaseStatus.description.maxLength} characters`}
                                    validationMessage={statusForm.errors.description}
                                >
                                    <Textarea
                                        name='description'
                                        rows={4}
                                        value={statusForm.values.description || ''}
                                        onChange={statusForm.handleChange}
                                        tabIndex={2}
                                        placeholder='Add a description of this status'
                                        maxLength={validateCaseStatus.description.maxLength}
                                        data-testid='add-edit-status-description-input'
                                    />
                                </Field>
                            </Column>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    type='submit'
                                    appearance='primary'
                                    onClick={clickSave}
                                    disabled={!statusForm.isValid || !statusForm.dirty}
                                    data-testid='add-edit-status-save-button'
                                >
                                    Save
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance='secondary'
                                    onClick={clickCancel}
                                    data-testid='add-edit-status-cancel-button'
                                >
                                    Cancel
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
